//@flow
import * as React from 'react';

import { withRouter } from 'common/components/core/router';
import { connect } from 'react-redux';
import { compose } from 'recompose';

import {
    clientSelector,
    editEntityIdSelector
} from 'common/redux/selectors/queryParamsSelectors';

import { type ThemeType } from 'react-css-themr';
import { type RouterHistory } from 'react-router-dom';

import TerritoryActions from 'common/redux/actions/territoryMaster';
import { CreateTerritoryPlan } from 'common/models/internal/CreateTerritoryPlan';
import TerritoryStepsContainer from './TerritoryStepsContainer';
import { ClientSelectors } from 'common/redux/selectors/clientSelector';
import ManualTerritoryCreation from './manual/ManualTerritoryCreation';
import { Loader } from 'common/components/core/loader';
import TerritoryAddEditProvider, {
    TerritoryAddEditContext
} from './TerritoryAddEditProvider';
import invariant from 'common/utils/invariant';

type Props = {
    theme: ThemeType,
    entityId?: string,
    clientId: string,
    history: RouterHistory,
    isEditing: boolean,
    reloadData: () => void,
    addAutomaticTerritoryPlan: $FixMe,
    addManualTerritoryPlan: $FixMe,
    fetchTerritory: $FixMe,
    updateManualTerritoryPlan: (
        territoryRequest: CreateTerritoryPlan,
        clientId: string,
        options: {
            onSuccess: (updatedPlan: CreateTerritoryPlan) => void,
            onFailure: (error: Error) => void
        }
    ) => Promise<{}>
};

const SingleTerritoryEditContainer = (props: Props): React.Node => {
    const { history } = props;

    const [territoryPlan, setTerritoryPlan] = React.useState(null);
    const [isManualPlan, setIsManualPlan] = React.useState(false);
    const [isFetchingData, setIsFetchingData] = React.useState(
        () => props.isEditing
    );
    const [postingData, setPostingData] = React.useState(false);
    const [apiError, setApiError] = React.useState('');

    const { reloadData } = props;

    const _showEditTerritory = React.useCallback(
        id => {
            reloadData();
            history.replace({
                search: `edit=${id}`
            });
        },
        [history, reloadData]
    );

    const { fetchTerritory } = props;

    const { enableNavigation, onDismissTerritoryCreation } = React.useContext(
        TerritoryAddEditContext
    );

    React.useEffect(() => {
        if (props.entityId) {
            const params = {
                clientId: props.clientId,
                territoryId: props.entityId
            };
            fetchTerritory(params, {
                onSuccess: plan => {
                    setTerritoryPlan(plan);
                    setIsFetchingData(false);
                },
                onFailure: () => {
                    setIsFetchingData(false);
                    onDismissTerritoryCreation();
                }
            });
        }
    }, [
        fetchTerritory,
        onDismissTerritoryCreation,
        props.clientId,
        props.entityId
    ]);

    const {
        addAutomaticTerritoryPlan,
        addManualTerritoryPlan,
        updateManualTerritoryPlan
    } = props;

    const _onSubmit = React.useCallback(
        config => {
            if (apiError) {
                setApiError('');
            }
            setPostingData(true);
            addAutomaticTerritoryPlan(config, {
                onSuccess: response => {
                    enableNavigation();
                    setPostingData(false);
                    _showEditTerritory(response.id);
                },
                onFailure: err => {
                    setPostingData(false);
                    setApiError(err.message);
                }
            });
        },
        [
            _showEditTerritory,
            addAutomaticTerritoryPlan,
            apiError,
            enableNavigation
        ]
    );

    const _onPlanFreeze = React.useCallback(() => {
        if (apiError) {
            setApiError('');
        }
        if (props.entityId) {
            setPostingData(true);
            const nextTerritoryPlan = territoryPlan?.setPlanStatus('FROZEN');
            if (nextTerritoryPlan) {
                updateManualTerritoryPlan(nextTerritoryPlan, props.clientId, {
                    onSuccess: plan => {
                        enableNavigation();
                        setTerritoryPlan(plan);
                        setPostingData(false);
                    },
                    onFailure: err => {
                        setPostingData(false);
                        setApiError(err.message);
                    }
                });
            } else {
                invariant(
                    territoryPlan,
                    'Found territory plan empty while freezing'
                );
            }
        }
    }, [
        apiError,
        enableNavigation,
        props.clientId,
        props.entityId,
        territoryPlan,
        updateManualTerritoryPlan
    ]);

    const _onManualSubmit = React.useCallback(
        territories => {
            if (apiError) {
                setApiError('');
            }
            setPostingData(true);
            if (props.entityId) {
                const nextTerritoryPlan = territoryPlan?.setTerritories(
                    territories.map(territory =>
                        territory
                            .setMetrics()
                            .setOutletsInfo(territory.outletsInfo.setOutlets())
                    )
                );
                if (nextTerritoryPlan) {
                    updateManualTerritoryPlan(
                        nextTerritoryPlan,
                        props.clientId,
                        {
                            onSuccess: () => {
                                enableNavigation();
                                setPostingData(false);
                            },
                            onFailure: err => {
                                setPostingData(false);
                                setApiError(err.message);
                            }
                        }
                    );
                } else {
                    invariant(
                        territoryPlan,
                        'Found territory plan empty while saving'
                    );
                }
            } else {
                const nextTerritoryPlan = territoryPlan?.setTerritories(
                    territories.map(territory => {
                        const _territory = territory
                            .setId()
                            .setOutletsInfo(territory.outletsInfo.setOutlets());
                        return _territory;
                    })
                );
                addManualTerritoryPlan(nextTerritoryPlan, props.clientId, {
                    onSuccess: response => {
                        enableNavigation();
                        setPostingData(false);
                        _showEditTerritory(response.id);
                    },
                    onFailure: err => {
                        setPostingData(false);
                        setApiError(err.message);
                    }
                });
            }
        },
        [
            apiError,
            territoryPlan,
            props.entityId,
            props.clientId,
            updateManualTerritoryPlan,
            enableNavigation,
            addManualTerritoryPlan,
            _showEditTerritory
        ]
    );

    const _showManualEditPlan = React.useCallback(() => {
        const emptyTerritoryPlan = {
            clientId: props.clientId,
            name: 'New plan',
            territories: []
        };
        setIsManualPlan(true);
        setTerritoryPlan(CreateTerritoryPlan.fromJS(emptyTerritoryPlan));
    }, [props.clientId]);

    const _onPlanNameChange = React.useCallback(
        value => {
            setTerritoryPlan(territoryPlan?.setName(value));
        },
        [territoryPlan]
    );

    if (isFetchingData) {
        return <Loader active style={{ left: 0 }} />;
    }

    if (territoryPlan) {
        return (
            <ManualTerritoryCreation
                onManualPlanSave={_onManualSubmit}
                onManualPlanFreeze={_onPlanFreeze}
                territoryPlan={territoryPlan}
                isEditing={props.isEditing}
                isManualPlan={isManualPlan}
                clientId={props.clientId}
                postingData={postingData}
                onPlanNameChange={_onPlanNameChange}
            />
        );
    }

    return (
        <TerritoryStepsContainer
            clientId={props.clientId}
            onSubmit={_onSubmit}
            apiError={apiError}
            postingData={postingData}
            showManualEditPlan={_showManualEditPlan}
        />
    );
};

const SingleTerritoryEditContainerWithProvider = (props: Props) => (
    <TerritoryAddEditProvider reloadMaster={props.reloadData}>
        <SingleTerritoryEditContainer {...props} />
    </TerritoryAddEditProvider>
);

const mapStateToProps = (state, ownProps) => {
    const entityId = editEntityIdSelector(state, ownProps);
    const clientId = clientSelector(state, ownProps);
    const defaultCountryCenter = ClientSelectors.getCountryCenterFromLocaleSettings(
        state,
        ownProps
    );
    const isEditing = Boolean(entityId);
    return { entityId, clientId, isEditing, defaultCountryCenter };
};

const mapDispatchToProps = {
    addAutomaticTerritoryPlan: TerritoryActions.addAutomaticTerritoryPlan,
    updateManualTerritoryPlan: TerritoryActions.updateManualTerritoryPlan,
    addManualTerritoryPlan: TerritoryActions.addManualTerritoryPlan,
    fetchTerritory: TerritoryActions.fetchTerritory
};

export default (compose(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps)
)(SingleTerritoryEditContainerWithProvider): React.AbstractComponent<>);
